import { UserRole } from '../../lib/types';

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
}

export interface NavPhase {
  id: string;
  label: string;
  icon: string;
  description: string;
  items: NavItem[];
}

export interface NavConfig {
  [role: string]: NavPhase[];
}

// Define all workflow phases with their structure
const phases = {
  discovery: {
    id: 'discovery',
    label: 'Discovery',
    icon: '💡',
    description: 'Capture and evaluate new ideas',
    items: {
      ideasFeedback: { id: 'ideas-feedback', label: 'Ideas & Feedback', icon: '💡', path: '/ideas' },
      discoveryKanban: { id: 'discovery-kanban', label: 'Discovery Kanban', icon: '📋', path: '/discovery-kanban' },
      domainIdeas: { id: 'domain-ideas', label: 'Domain Ideas', icon: '🌐', path: '/domain-ideas' },
      roadmap: { id: 'roadmap', label: 'Roadmap', icon: '🗺️', path: '/roadmap' },
    },
  },
  governance: {
    id: 'governance',
    label: 'Governance',
    icon: '🎯',
    description: 'Assessment and decision gates',
    items: {
      assessment: { id: 'assessment', label: 'Assessment', icon: '🎯', path: '/assessment' },
      gateDecisions: { id: 'gate-decisions', label: 'Gate Decisions', icon: '🎯', path: '/gate-decisions' },
      forumAgenda: { id: 'forum-agenda', label: 'Forum Agenda', icon: '📋', path: '/forum-agenda' },
      businessCase: { id: 'business-case', label: 'Business Case', icon: '📋', path: '/business-case' },
    },
  },
  portfolio: {
    id: 'portfolio',
    label: 'Portfolio',
    icon: '📊',
    description: 'Active projects and planning',
    items: {
      pipeline: { id: 'pipeline', label: 'Pipeline', icon: '🔄', path: '/pipeline' },
      activeProjects: { id: 'active-projects', label: 'Active Projects', icon: '📊', path: '/active-projects' },
      timesheets: { id: 'timesheets', label: 'Timesheets', icon: '📊', path: '/timesheets' },
      myProjects: { id: 'my-projects', label: 'My Projects', icon: '📊', path: '/my-projects' },
      teamCapacity: { id: 'team-capacity', label: 'Team Capacity', icon: '📊', path: '/team-capacity' },
      pendingTimesheets: { id: 'pending-timesheets', label: 'Pending Timesheets', icon: '📊', path: '/pending-timesheets' },
      teamProjects: { id: 'team-projects', label: 'Team Projects', icon: '📊', path: '/team-projects' },
    },
  },
  reporting: {
    id: 'reporting',
    label: 'Reporting',
    icon: '📈',
    description: 'Analytics and health reports',
    items: {
      myHours: { id: 'my-hours', label: 'My Hours', icon: '📈', path: '/my-hours' },
      teamHealth: { id: 'team-health', label: 'Team Health', icon: '📈', path: '/team-health' },
      analytics: { id: 'analytics', label: 'Analytics', icon: '📈', path: '/analytics' },
      healthReports: { id: 'health-reports', label: 'Health Reports', icon: '📈', path: '/health-reports' },
      portfolioAnalytics: { id: 'portfolio-analytics', label: 'Portfolio Analytics', icon: '📈', path: '/portfolio-analytics' },
      healthDashboard: { id: 'health-dashboard', label: 'Health Dashboard', icon: '📈', path: '/health-dashboard' },
      deptAnalytics: { id: 'dept-analytics', label: 'Dept Analytics', icon: '📈', path: '/dept-analytics' },
    },
  },
  admin: {
    id: 'admin',
    label: 'Admin',
    icon: '⚙️',
    description: 'System administration',
    items: {
      departments: { id: 'departments', label: 'Departments', icon: '🏢', path: '/departments' },
      teams: { id: 'teams', label: 'Teams', icon: '👥', path: '/teams' },
      projectManagers: { id: 'project-managers', label: 'Project Managers', icon: '👤', path: '/project-managers' },
      workCategories: { id: 'work-categories', label: 'Work Categories', icon: '📑', path: '/work-categories' },
      strategicGoals: { id: 'strategic-goals', label: 'Strategic Goals', icon: '🎯', path: '/strategic-goals' },
      capitalProjects: { id: 'capital-projects', label: 'Capital Projects', icon: '💰', path: '/capital-projects' },
    },
  },
};

// Role-based navigation configuration
export const navigationConfig: NavConfig = {
  flow_TeamMember: [
    {
      ...phases.portfolio,
      items: [phases.portfolio.items.timesheets, phases.portfolio.items.myProjects],
    },
    {
      ...phases.reporting,
      items: [phases.reporting.items.myHours],
    },
  ],
  flow_TeamLead: [
    {
      ...phases.portfolio,
      items: [
        phases.portfolio.items.teamCapacity,
        phases.portfolio.items.pendingTimesheets,
        phases.portfolio.items.myProjects,
      ],
    },
    {
      ...phases.reporting,
      items: [phases.reporting.items.teamHealth, phases.reporting.items.analytics],
    },
  ],
  flow_ProjectManager: [
    {
      ...phases.discovery,
      items: [phases.discovery.items.ideasFeedback, phases.discovery.items.discoveryKanban, phases.discovery.items.roadmap],
    },
    {
      ...phases.governance,
      items: [phases.governance.items.assessment, phases.governance.items.gateDecisions, phases.governance.items.forumAgenda, phases.governance.items.businessCase],
    },
    {
      ...phases.portfolio,
      items: [phases.portfolio.items.pipeline, phases.portfolio.items.activeProjects, phases.portfolio.items.timesheets],
    },
    {
      ...phases.reporting,
      items: [phases.reporting.items.healthReports, phases.reporting.items.analytics],
    },
  ],
  flow_DepartmentLead: [
    {
      ...phases.governance,
      items: [phases.governance.items.gateDecisions],
    },
    {
      ...phases.portfolio,
      items: [phases.portfolio.items.teamProjects, phases.portfolio.items.teamCapacity],
    },
    {
      ...phases.reporting,
      items: [phases.reporting.items.deptAnalytics],
    },
    {
      ...phases.admin,
      items: [phases.admin.items.teams],
    },
  ],
  flow_Admin: [
    {
      ...phases.discovery,
      items: [phases.discovery.items.ideasFeedback, phases.discovery.items.discoveryKanban, phases.discovery.items.domainIdeas, phases.discovery.items.roadmap],
    },
    {
      ...phases.governance,
      items: [phases.governance.items.assessment, phases.governance.items.gateDecisions, phases.governance.items.forumAgenda, phases.governance.items.businessCase],
    },
    {
      ...phases.portfolio,
      items: [
        phases.portfolio.items.pipeline,
        phases.portfolio.items.activeProjects,
        phases.portfolio.items.timesheets,
        phases.portfolio.items.myProjects,
        phases.portfolio.items.teamCapacity,
        phases.portfolio.items.pendingTimesheets,
        phases.portfolio.items.teamProjects,
      ],
    },
    {
      ...phases.reporting,
      items: [
        phases.reporting.items.myHours,
        phases.reporting.items.teamHealth,
        phases.reporting.items.analytics,
        phases.reporting.items.healthReports,
        phases.reporting.items.portfolioAnalytics,
        phases.reporting.items.healthDashboard,
        phases.reporting.items.deptAnalytics,
      ],
    },
    {
      ...phases.admin,
      items: [
        phases.admin.items.departments,
        phases.admin.items.teams,
        phases.admin.items.projectManagers,
        phases.admin.items.workCategories,
        phases.admin.items.strategicGoals,
        phases.admin.items.capitalProjects,
      ],
    },
  ],
  flow_GovernanceCommittee: [
    {
      ...phases.governance,
      items: [phases.governance.items.assessment, phases.governance.items.gateDecisions, phases.governance.items.forumAgenda],
    },
    {
      ...phases.reporting,
      items: [phases.reporting.items.portfolioAnalytics, phases.reporting.items.healthDashboard],
    },
  ],
  flow_DomainOwner: [
    {
      ...phases.discovery,
      items: [phases.discovery.items.ideasFeedback, phases.discovery.items.domainIdeas, phases.discovery.items.roadmap],
    },
    {
      ...phases.governance,
      items: [phases.governance.items.gateDecisions],
    },
    {
      ...phases.reporting,
      items: [phases.reporting.items.healthReports],
    },
  ],
  flow_FunctionLead: [
    {
      ...phases.governance,
      items: [phases.governance.items.gateDecisions],
    },
    {
      ...phases.portfolio,
      items: [phases.portfolio.items.teamCapacity],
    },
    {
      ...phases.reporting,
      items: [phases.reporting.items.deptAnalytics],
    },
    {
      ...phases.admin,
      items: [phases.admin.items.teams],
    },
  ],
  flow_PublicUser: [
    {
      ...phases.discovery,
      items: [phases.discovery.items.ideasFeedback, phases.discovery.items.roadmap],
    },
  ],
  flow_SuperUser: [
    {
      ...phases.discovery,
      items: [phases.discovery.items.ideasFeedback, phases.discovery.items.discoveryKanban, phases.discovery.items.domainIdeas, phases.discovery.items.roadmap],
    },
    {
      ...phases.governance,
      items: [phases.governance.items.assessment, phases.governance.items.gateDecisions, phases.governance.items.forumAgenda, phases.governance.items.businessCase],
    },
    {
      ...phases.portfolio,
      items: [
        phases.portfolio.items.pipeline,
        phases.portfolio.items.activeProjects,
        phases.portfolio.items.timesheets,
        phases.portfolio.items.myProjects,
        phases.portfolio.items.teamCapacity,
        phases.portfolio.items.pendingTimesheets,
        phases.portfolio.items.teamProjects,
      ],
    },
    {
      ...phases.reporting,
      items: [
        phases.reporting.items.myHours,
        phases.reporting.items.teamHealth,
        phases.reporting.items.analytics,
        phases.reporting.items.healthReports,
        phases.reporting.items.portfolioAnalytics,
        phases.reporting.items.healthDashboard,
        phases.reporting.items.deptAnalytics,
      ],
    },
    {
      ...phases.admin,
      items: [
        phases.admin.items.departments,
        phases.admin.items.teams,
        phases.admin.items.projectManagers,
        phases.admin.items.workCategories,
        phases.admin.items.strategicGoals,
        phases.admin.items.capitalProjects,
      ],
    },
  ],
};

/**
 * Get the navigation configuration for a specific user role
 * @param role - The user's role
 * @returns Navigation phases and items visible for that role
 */
export function getNavigationForRole(role: UserRole | string): NavPhase[] {
  return navigationConfig[role] || navigationConfig.TeamMember;
}

/**
 * Get all available phases (for admin use)
 */
export function getAllPhases(): NavPhase[] {
  return Object.values(phases);
}

/**
 * Check if a navigation item is accessible for a given role
 * @param role - The user's role
 * @param phaseId - The phase ID
 * @param itemId - The item ID
 * @returns true if the item is accessible
 */
export function isItemAccessibleForRole(
  role: UserRole | string,
  phaseId: string,
  itemId: string
): boolean {
  const navConfig = getNavigationForRole(role);
  const phase = navConfig.find((p) => p.id === phaseId);
  if (!phase) return false;
  return phase.items.some((item) => item.id === itemId);
}
